import React from 'react';
import { Check } from '../../shared/components/Icons';
import { InfoTooltip } from '../../shared/components/InfoTooltip';
import { api } from '../../shared/utils/api';

const PROVIDERS = ['BARBRI', 'BPP', 'Kaplan', 'Revise SQE', 'Epimeleia', 'ULaw', 'Other'];

export default function PlannerSettings({
  flk1Date, setFlk1Date, flk2Date, setFlk2Date, saved, saveDates,
  provider, setProvider, saveSetting,
  mcqTarget, setMcqTarget, saveMcqTarget,
  targetConf, setTargetConf, customTargetVal, setCustomTargetVal,
  allStatuses, newStatus, setNewStatus, addStatus, moveStatusUp, moveStatusDown, removeStatus, saveAllStatuses,
  srRedDays, setSrRedDays, srAmberDays, setSrAmberDays, saveSpacedRep,
  settings, setSettings,
  plannerStudyDays, setPlannerStudyDays, plannerMaxSessions, setPlannerMaxSessions,
  plannerExamBuffer, setPlannerExamBuffer, plannerRevisionThreshold, setPlannerRevisionThreshold,
  plannerRecommendation, setPlannerRecommendation, plannerSettingsSaved, plannerSettingsSaving, savePlannerSettings,
  focusTopicCount, setFocusTopicCount,
  flash,
  getDefaultColor,
}) {
  return (
    <>
      <div className="settings-section">
        <div className="settings-section-title">Exam dates</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">FLK1</span>
          </div>
          <div className="settings-row-right">
            <input className="settings-date-input" type="date" value={flk1Date}
              onChange={e => setFlk1Date(e.target.value)} onBlur={saveDates} />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">FLK2</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-date-input" type="date" value={flk2Date}
              onChange={e => setFlk2Date(e.target.value)} onBlur={saveDates} />
            {saved['dates'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Study materials</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Provider</span>
            <span className="settings-row-key">Used for MCQ import guidance</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <select className="settings-select" value={provider}
              onChange={e => { setProvider(e.target.value); saveSetting('material_provider', e.target.value); }}>
              <option value="">— Not set —</option>
              {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {saved['material_provider'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">MCQ practice</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Daily MCQ target</span>
            <span className="settings-row-key">Questions you aim to complete each day</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="999" value={mcqTarget}
              onChange={e => setMcqTarget(e.target.value)}
              onBlur={saveMcqTarget}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            {saved['mcq_daily_target'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Preparation target <InfoTooltip text="The confidence level you are aiming for on each topic. Used to calculate your on-track percentage on the dashboard." /></div>
        <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-2)' }}>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Sets the confidence level that counts as "ready" in the planner. SQE1 is pass/fail — consistent accuracy across all areas matters more than perfection.
          </p>
          <div className="prep-target-options">
            {[
              { value: '0.60', label: 'Solid', desc: 'Confident on the essentials. A good baseline for passing.' },
              { value: '0.70', label: 'Well prepared', desc: 'Strong across all areas. Recommended for most candidates.' },
              { value: '0.80', label: 'Very prepared', desc: 'High confidence standard. For candidates aiming well above the pass mark.' },
            ].map(opt => (
              <label key={opt.value} className={`prep-target-btn ${targetConf === opt.value ? 'selected' : ''}`}>
                <input type="radio" name="target_conf" value={opt.value} checked={targetConf === opt.value}
                  onChange={() => { setTargetConf(opt.value); saveSetting('target_confidence', opt.value); }}
                  style={{ display: 'none' }} />
                <span className="prep-target-label">{opt.label} <span className="prep-target-pct">{Math.round(parseFloat(opt.value) * 100)}%</span></span>
                <span className="prep-target-desc">{opt.desc}</span>
              </label>
            ))}
            <label key="custom" className={`prep-target-btn ${!['0.60','0.70','0.80'].includes(targetConf) ? 'selected' : ''}`}>
              <input type="radio" name="target_conf" value="custom"
                checked={!['0.60','0.70','0.80'].includes(targetConf)}
                onChange={() => {
                  const v = customTargetVal || '65';
                  setCustomTargetVal(v);
                  const fv = (parseInt(v) / 100).toFixed(2);
                  setTargetConf(fv);
                  saveSetting('target_confidence', fv);
                }}
                style={{ display: 'none' }} />
              <span className="prep-target-label">Custom <span className="prep-target-pct">{!['0.60','0.70','0.80'].includes(targetConf) ? Math.round(parseFloat(targetConf)*100) + '%' : ''}</span></span>
              <span className="prep-target-desc">Set your own confidence level.</span>
              {!['0.60','0.70','0.80'].includes(targetConf) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                  <input
                    type="number" min="50" max="95" className="settings-number-input"
                    value={customTargetVal}
                    onChange={e => setCustomTargetVal(e.target.value)}
                    onBlur={() => {
                      const v = Math.max(50, Math.min(95, parseInt(customTargetVal) || 65));
                      setCustomTargetVal(String(v));
                      const fv = (v / 100).toFixed(2);
                      setTargetConf(fv);
                      saveSetting('target_confidence', fv);
                    }}
                    style={{ width: 60 }}
                  />
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>%</span>
                </div>
              )}
            </label>
          </div>
          {saved['target_confidence'] && <span className="settings-inline-saved" style={{ marginTop: 8, display: 'inline-block' }}><Check size={12} /> Saved</span>}
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Topic statuses</div>
        <div style={{ padding: 'var(--sp-3) var(--sp-4) var(--sp-2)' }}>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Customise the statuses shown in your topic tracker. Drag to reorder, or add your own.
          </p>
          <div className="status-order-list">
            {allStatuses.map((statusObj, idx) => (
              <div key={(statusObj.label || statusObj) + idx} className="status-order-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="color"
                    value={statusObj.color || getDefaultColor(idx)}
                    style={{ width: 22, height: 22, padding: 1, border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', background: 'none' }}
                    onChange={e => {
                      const updated = allStatuses.map((x, i) => i === idx ? { ...x, color: e.target.value } : x);
                      saveAllStatuses(updated);
                    }}
                    title="Pick colour"
                  />
                  <span className="status-order-name">{statusObj.label || statusObj}</span>
                </div>
                <div className="status-order-controls">
                  <button className="status-order-btn" onClick={() => moveStatusUp(idx)} disabled={idx === 0} title="Move up">↑</button>
                  <button className="status-order-btn" onClick={() => moveStatusDown(idx)} disabled={idx === allStatuses.length - 1} title="Move down">↓</button>
                  <button className="status-order-btn status-order-remove" onClick={() => removeStatus(idx)} title="Remove">×</button>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <input className="settings-text-input" value={newStatus} placeholder="New status name"
              onChange={e => setNewStatus(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addStatus()}
              style={{ width: 160 }} />
            <button className="settings-action-btn" onClick={addStatus} disabled={!newStatus.trim()}>Add</button>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Spaced repetition</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Weak topics (under 50%)</span>
            <span className="settings-row-key">Days between revisits in the planner and recall nudge</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="90" value={srRedDays}
              onChange={e => setSrRedDays(e.target.value)}
              onBlur={saveSpacedRep}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>days</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Moderate topics (50–69%)</span>
            <span className="settings-row-key">Days between revisits in the planner and recall nudge</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="1" max="90" value={srAmberDays}
              onChange={e => setSrAmberDays(e.target.value)}
              onBlur={saveSpacedRep}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>days</span>
            {saved['spaced_rep_red_days'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Planner</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Planner session level</span>
            <span className="settings-row-key">Choose whether planned sessions target broad topic areas or specific subtopics</span>
          </div>
          <div className="settings-row-right" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
            {[
              { value: 'L2', label: 'Topic areas (e.g. Sale of Goods)', desc: 'Plan sessions around broader topic areas (recommended)' },
              { value: 'L3', label: 'Subtopics (e.g. Implied terms under SGA 1979)', desc: 'Plan sessions around specific subtopics' },
            ].map(opt => (
              <label key={opt.value} className="settings-radio-label">
                <input
                  type="radio"
                  name="planner_detail_level"
                  value={opt.value}
                  checked={(settings.planner_detail_level || 'L2') === opt.value}
                  onChange={() => { setSettings(prev => ({ ...prev, planner_detail_level: opt.value })); saveSetting('planner_detail_level', opt.value); }}
                />
                <span className="settings-radio-text">
                  <span className="settings-radio-name">{opt.label}</span>
                  <span className="settings-radio-desc">{opt.desc}</span>
                </span>
              </label>
            ))}
            {saved['planner_detail_level'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Planner schedule</div>

        {/* Study days */}
        <div className="settings-row" style={{ alignItems: 'flex-start' }}>
          <div className="settings-row-left">
            <span className="settings-row-label">Study days</span>
            <span className="settings-row-key">Days you plan to study each week</span>
          </div>
          <div className="settings-row-right" style={{ flexWrap: 'wrap', gap: 6 }}>
            {['mon','tue','wed','thu','fri','sat','sun'].map(day => {
              const selected = plannerStudyDays.includes(day);
              return (
                <button
                  key={day}
                  className={`planner-day-toggle ${selected ? 'selected' : ''}`}
                  onClick={() => {
                    const next = selected
                      ? plannerStudyDays.filter(d => d !== day)
                      : [...plannerStudyDays, day];
                    const order = ['mon','tue','wed','thu','fri','sat','sun'];
                    const ordered = order.filter(d => next.includes(d));
                    if (ordered.length === 0) return;
                    setPlannerStudyDays(ordered);
                    savePlannerSettings({ study_days: ordered });
                  }}
                >
                  {day.charAt(0).toUpperCase() + day.slice(1)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sessions per day */}
        <div className="settings-row" style={{ alignItems: 'flex-start' }}>
          <div className="settings-row-left">
            <span className="settings-row-label">Approximate sessions per day</span>
            <span className="settings-row-key">SQETrack adjusts this based on topic depth. Bigger topics count as more than one session.</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input
              className="settings-number-input"
              type="number" min="1" max="20"
              value={plannerMaxSessions}
              onChange={e => setPlannerMaxSessions(parseInt(e.target.value) || 8)}
              onBlur={() => savePlannerSettings({ max_sessions_per_day: plannerMaxSessions })}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
            />
          </div>
        </div>

        {/* Sessions recommendation */}
        {plannerRecommendation && (
          <div style={{ padding: '0 var(--sp-4) var(--sp-3)' }}>
            <div style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius)',
              background: plannerRecommendation.status === 'ok' ? '#F0FDF4' : '#FEF3C7',
              border: `1px solid ${plannerRecommendation.status === 'ok' ? '#86EFAC' : '#FCD34D'}`,
              fontSize: 12,
              color: plannerRecommendation.status === 'ok' ? '#15803d' : '#92400E',
              lineHeight: 1.5,
            }}>
              {plannerRecommendation.message}
              {plannerRecommendation.coverage_deadline && (
                <> Coverage deadline: {new Date(plannerRecommendation.coverage_deadline + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.</>
              )}
            </div>
          </div>
        )}

        {/* Cover all topics by */}
        <div className="settings-row" style={{ alignItems: 'flex-start' }}>
          <div className="settings-row-left">
            <span className="settings-row-label">Cover all topics by</span>
            <span className="settings-row-key">All topics will be scheduled before this date. After this point, the planner switches to revision only.</span>
            {plannerRecommendation?.coverage_deadline && (
              <span className="settings-row-key" style={{ color: 'var(--accent)', marginTop: 2 }}>
                All topics covered by {new Date(plannerRecommendation.coverage_deadline + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
              </span>
            )}
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input
              className="settings-number-input"
              type="number" min="0" max="30"
              value={plannerExamBuffer}
              onChange={e => setPlannerExamBuffer(parseInt(e.target.value) || 14)}
              onBlur={() => savePlannerSettings({ exam_buffer_days: plannerExamBuffer })}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()}
            />
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>days before exam</span>
          </div>
        </div>

        {/* Revision blend threshold */}
        <div className="settings-row" style={{ alignItems: 'flex-start' }}>
          <div className="settings-row-left">
            <span className="settings-row-label">Start revision after covering</span>
            <span className="settings-row-key">Before this point, the planner focuses on new material. After, it starts blending in revision of topics you have already covered.</span>
          </div>
          <div className="settings-row-right" style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            {[
              { value: 0.10, label: '10% of topics (~50)' },
              { value: 0.20, label: '20% of topics (~100)' },
              { value: 0.30, label: '30% of topics (~150)' },
              { value: 0.50, label: '50% of topics (~250)' },
            ].map(opt => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="revision_threshold"
                  value={opt.value}
                  checked={Math.abs(plannerRevisionThreshold - opt.value) < 0.01}
                  onChange={() => {
                    setPlannerRevisionThreshold(opt.value);
                    savePlannerSettings({ revision_start_threshold: opt.value });
                  }}
                />
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Regenerate button */}
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Regenerate plan</span>
            <span className="settings-row-key">Rebuilds your revision schedule from today. Completed topics are preserved.</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <button
              className="settings-action-btn"
              onClick={async () => {
                await api('/api/planner/regenerate', { method: 'POST' });
                flash('planner_regenerate');
                api('/api/planner/settings').then(ps => {
                  if (ps?._recommendation) setPlannerRecommendation(ps._recommendation);
                }).catch(() => {});
              }}
            >
              Regenerate
            </button>
            {saved['planner_regenerate'] && <span className="settings-inline-saved"><Check size={12} /> Done</span>}
          </div>
        </div>

        {plannerSettingsSaved && (
          <div style={{ padding: '4px 16px 8px' }}>
            <span className="settings-inline-saved"><Check size={12} /> Planner updated</span>
          </div>
        )}
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Dashboard</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Focus topics per module</span>
            <span className="settings-row-key">How many priority topics to show in each FLK focus list on the dashboard</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8 }}>
            <input className="settings-number-input" type="number" min="5" max="20" value={focusTopicCount}
              onChange={e => setFocusTopicCount(e.target.value)}
              onBlur={() => {
                const v = Math.max(5, Math.min(20, parseInt(focusTopicCount) || 10)).toString();
                setFocusTopicCount(v);
                saveSetting('focus_topic_count', v);
              }}
              onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            {saved['focus_topic_count'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>
    </>
  );
}
